import React, { forwardRef } from 'react';
import { goodReceive } from '../../model/good-receive';
import { convertFormatDateTime } from '../../utils/util';
import { printDocument } from '../../utils/print-utils';
import { formatActor, UNKNOWN_ACTOR_SHORT } from '../../utils/audit-format';

interface Props {
    data: goodReceive;
    onClose?: () => void;
}

export const printGoodReceiveDocument = (contentElement: HTMLElement | null) => {
    printDocument(contentElement, {
        documentType: 'a4-portrait',
        title: `ใบรับเข้าสินค้า ${contentElement?.querySelector('.gr-print-doc-no')?.textContent || ''}`
    });
};

const GoodReceivePrint = forwardRef<HTMLDivElement, Props>(({ data }, ref) => {
    const totalQty = data.quantity || data.detail?.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0) || 0;
    const totalCost = data.cost || data.detail?.reduce((sum, item) => sum + ((Number(item.quantity) || 0) * (Number(item.cost) || 0)), 0) || 0;

    return (
        <div 
            ref={ref}
            className="gr-print-container" 
            data-testid="gr-print-document"
            style={{ 
                padding: '24px', 
                background: '#FFFFFF', 
                color: '#1E293B', 
                fontFamily: 'Kodchasan, Prompt, sans-serif' 
            }}
        >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #FF7A59', paddingBottom: '16px', marginBottom: '20px' }}>
                <div>
                    <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#FF7A59', margin: 0 }}>🥕 JinDa POS Store</h1>
                    <p style={{ fontSize: '12px', color: '#64748B', margin: '4px 0 0 0' }}>
                        123/45 ถนนสุขุมวิท กรุงเทพมหานคร 10110 | โทร: 02-123-4567 | เลขผู้เสียภาษี: 0105559999999
                    </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1E293B', margin: 0 }}>ใบรับเข้าสินค้า (GOODS RECEIVE)</h2>
                    <p className="gr-print-doc-no" style={{ fontSize: '13px', fontWeight: 600, color: '#FF7A59', margin: '4px 0 0 0' }}>
                        เลขที่: {data.document_no || 'GR-DRAFT'}
                    </p>
                    <p style={{ fontSize: '12px', color: '#64748B', margin: 0 }}>
                        วันที่รับเข้า: {typeof data.create_date === 'string' ? data.create_date : (data.create_date ? convertFormatDateTime(data.create_date) : new Date().toLocaleDateString('th-TH'))}
                    </p>
                    {data.purchase_order_doc_no ? (
                        <p style={{ fontSize: '12px', color: '#2563EB', fontWeight: 600, margin: '2px 0 0 0' }}>
                            อ้างอิง PO: {data.purchase_order_doc_no}
                        </p>
                    ) : null}
                </div>
            </div>

            {/* Vendor & Reception Info */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', background: '#F8FAFC', padding: '14px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #E2E8F0', fontSize: '13px' }}>
                <div>
                    <strong style={{ color: '#FF7A59' }}>ผู้จัดจำหน่าย / ผู้ส่งมอบ (Vendor):</strong>
                    <div style={{ fontWeight: 600, marginTop: '2px' }}>{data.vendor_name || 'ไม่ได้ระบุ'}</div>
                    <div style={{ color: '#64748B' }}>สถานะ: {data.status === 2 ? 'รับเข้าคลังเรียบร้อย' : 'ฉบับร่าง'}</div>
                </div>
                <div>
                    <strong style={{ color: '#FF7A59' }}>ข้อมูลการตรวจรับ / สถานที่:</strong>
                    <div style={{ color: '#334155', marginTop: '2px' }}>คลังสินค้า: สาขาหลัก JinDa POS Store</div>
                    {/* Phase 7C-6C: this printed "ผู้ดูแลระบบ" on every
                        slip, because `create_by` was never written and the
                        name was therefore always null -- a document handed
                        to a supplier naming a person who had done nothing.
                        7C-6B records the real operator now; older receipts
                        say so instead of naming someone. */}
                    <div style={{ color: '#64748B' }} data-testid="good-receive-actor">
                        ผู้ทำรายการ: {formatActor(data.create_by_name, UNKNOWN_ACTOR_SHORT)}
                    </div>
                    <div style={{ color: '#64748B' }}>หมายเหตุ: {data.remark || '-'}</div>
                </div>
            </div>

            {/* Items Table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px', fontSize: '13px' }}>
                <thead>
                    <tr style={{ background: '#FFF3EE', borderBottom: '2px solid #FFCBBB', color: '#FF7A59' }}>
                        <th style={{ padding: '8px 10px', textAlign: 'center', width: '50px' }}>ลำดับ</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left', width: '130px' }}>รหัสสินค้า</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left' }}>รายการสินค้า</th>
                        <th style={{ padding: '8px 10px', textAlign: 'center', width: '90px' }}>จำนวนรับ</th>
                        <th style={{ padding: '8px 10px', textAlign: 'right', width: '110px' }}>ต้นทุน/หน่วย</th>
                        <th style={{ padding: '8px 10px', textAlign: 'right', width: '120px' }}>รวม (บาท)</th>
                    </tr>
                </thead>
                <tbody>
                    {data.detail && data.detail.length > 0 ? (
                        data.detail.map((item, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #E2E8F0', pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                                <td style={{ padding: '8px 10px', textAlign: 'center' }}>{idx + 1}</td>
                                <td style={{ padding: '8px 10px', fontWeight: 600 }}>{item.product_code}</td>
                                <td style={{ padding: '8px 10px' }}>{item.product_name}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700 }}>{item.quantity}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right' }}>{Number(item.cost || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>
                                    {Number((item.quantity * item.cost) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={6} style={{ textAlign: 'center', padding: '20px', color: '#94A3B8' }}>ไม่มีรายการสินค้า</td>
                        </tr>
                    )}
                </tbody>
            </table>

            {/* Totals Summary */}
            <div className="gr-totals-section" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '36px', pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                <div style={{ width: '280px', fontSize: '13px', lineHeight: '1.8' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748B' }}>
                        <span>จำนวนสินค้าทั้งหมด:</span>
                        <strong style={{ color: '#1E293B' }}>{Number(totalQty).toLocaleString()} ชิ้น</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: 700, color: '#FF7A59', borderTop: '2px solid #FFD8CC', paddingTop: '8px', marginTop: '4px' }}>
                        <span>มูลค่ารับเข้าสุทธิ:</span>
                        <span>{Number(totalCost).toLocaleString('en-US', { minimumFractionDigits: 2 })} ฿</span>
                    </div>
                </div>
            </div>

            {/* Signatures */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', textAlign: 'center', fontSize: '12px', color: '#64748B', pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                <div>
                    <div style={{ borderBottom: '1px dotted #94A3B8', height: '50px', marginBottom: '8px' }}></div>
                    <p style={{ margin: 0, fontWeight: 600, color: '#334155' }}>ผู้รับเข้าสินค้า / ผู้ตรวจนับ</p>
                    <p style={{ margin: '2px 0 0 0', fontSize: '11px' }}>วันที่: ......./......./...........</p>
                </div>
                <div>
                    <div style={{ borderBottom: '1px dotted #94A3B8', height: '50px', marginBottom: '8px' }}></div>
                    <p style={{ margin: 0, fontWeight: 600, color: '#334155' }}>ผู้ส่งมอบสินค้า / ซัพพลายเออร์</p>
                    <p style={{ margin: '2px 0 0 0', fontSize: '11px' }}>วันที่: ......./......./...........</p>
                </div>
                <div>
                    <div style={{ borderBottom: '1px dotted #94A3B8', height: '50px', marginBottom: '8px' }}></div>
                    <p style={{ margin: 0, fontWeight: 600, color: '#334155' }}>ผู้ตรวจสอบ / ผู้จัดการร้าน</p>
                    <p style={{ margin: '2px 0 0 0', fontSize: '11px' }}>วันที่: ......./......./...........</p>
                </div>
            </div>
        </div>
    );
});

GoodReceivePrint.displayName = 'GoodReceivePrint';

export default GoodReceivePrint;
